"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Info, X } from "lucide-react";

/**
 * Visual variant — only `info` is supported in V2. Reserved for future use.
 */
export type ToastBannerVariant = "info";

/**
 * Props for {@link ToastBanner}.
 */
export interface ToastBannerProps {
  /** Text content of the toast. */
  message: string;
  /** Controls visibility. The toast handles its own enter/exit animations. */
  open: boolean;
  /** Called when the user dismisses the toast or auto-hide expires. */
  onClose: () => void;
  /** Auto-hide timeout in ms (paused on hover). Defaults to 5000. */
  autoHideMs?: number;
  /** Visual variant. Defaults to `info`. */
  variant?: ToastBannerVariant;
}

const ENTER_DURATION_MS = 250;
const EXIT_DURATION_MS = 200;

/**
 * Top-centered glass toast used for the `ok_with_hints` MIDI quality flow.
 *
 * - Slides in from the top on `open=true`, fades out on `open=false`.
 * - Auto-dismisses after `autoHideMs` (paused while the user hovers).
 * - Click-to-dismiss via the `×` button. `Escape` is NOT handled here (toasts
 *   should not steal that key from modals); the host owns it.
 *
 * Accessible: `role="status"`, `aria-live="polite"`, `aria-atomic="true"`.
 */
export function ToastBanner({
  message,
  open,
  onClose,
  autoHideMs = 5000,
  variant = "info",
}: ToastBannerProps) {
  // Track render state separately from `open` so we can animate the exit
  // before unmounting the DOM.
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);
  const hoveredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();

    if (autoHideMs <= 0) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      onClose();
    }, autoHideMs);
  }, [autoHideMs, clearTimer, onClose]);

  // Mount / unmount + enter / exit animation orchestration.
  useEffect(() => {
    if (open) {
      setRendered(true);
      // Defer to next frame so the initial styles (translateY/opacity) apply
      // before transitioning to the "visible" state.
      const raf = requestAnimationFrame(() => {
        setVisible(true);
      });

      return () => {
        cancelAnimationFrame(raf);
      };
    }

    setVisible(false);
    const id = window.setTimeout(() => {
      setRendered(false);
    }, EXIT_DURATION_MS);

    return () => {
      window.clearTimeout(id);
    };
  }, [open]);

  // Auto-hide timer (re)started when the toast becomes visible.
  useEffect(() => {
    if (!visible) {
      clearTimer();

      return;
    }

    if (!hoveredRef.current) {
      startTimer();
    }

    return () => {
      clearTimer();
    };
  }, [visible, startTimer, clearTimer]);

  const handleMouseEnter = useCallback(() => {
    hoveredRef.current = true;
    clearTimer();
  }, [clearTimer]);

  const handleMouseLeave = useCallback(() => {
    hoveredRef.current = false;

    if (visible) {
      startTimer();
    }
  }, [visible, startTimer]);

  if (!rendered) {
    return null;
  }

  // We avoid relying on global `toast-enter` / `toast-exit` keyframes so the
  // atom stays self-contained. Transitions are inline.
  const transform = visible
    ? "translate(-50%, 0)"
    : "translate(-50%, -16px)";
  const opacity = visible ? 1 : 0;
  const transitionDuration = visible
    ? `${ENTER_DURATION_MS}ms`
    : `${EXIT_DURATION_MS}ms`;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-variant={variant}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="fixed z-50 inline-flex items-center gap-2.5"
      style={{
        top: 24,
        left: "50%",
        maxWidth: 480,
        padding: "12px 18px",
        borderRadius: "var(--radius-tile)",
        backgroundColor: "var(--color-glass-surface)",
        border: "1px solid var(--color-glass-border)",
        boxShadow: "var(--shadow-popin)",
        transform,
        opacity,
        transition: `transform ${transitionDuration} ease-out, opacity ${transitionDuration} ease-out`,
        willChange: "transform, opacity",
      }}
    >
      <Info
        size={14}
        strokeWidth={1.8}
        aria-hidden
        style={{
          color: "var(--color-accent-bright)",
          flexShrink: 0,
        }}
      />
      <span
        className="text-[13px] font-medium"
        style={{ color: "var(--color-text-primary)" }}
      >
        {message}
      </span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer la notification"
        className="inline-flex items-center justify-center rounded-md transition-colors duration-150"
        style={{
          width: 24,
          height: 24,
          color: "var(--color-text-muted)",
          marginLeft: 4,
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.color = "var(--color-text-primary)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.color = "var(--color-text-muted)";
        }}
      >
        <X size={14} strokeWidth={1.8} aria-hidden />
      </button>
    </div>
  );
}
